import { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  CheckIcon,
  ChevronDownIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
  Trash2Icon,
} from "lucide-react";
import { FieldPath, useFieldArray, useForm } from "react-hook-form";
import { useDebounce } from "use-debounce";
import { z } from "zod";

import {
  Column,
  ConfirmationDialog,
  RichTextEditorInput,
  Row,
} from "@/components";
import { FormStarRatingInput } from "@/components/form/FormStarRatingInput";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { useUpdateCandidacy } from "@/hooks/mutations/useUpdateCandidacy";
import { useSetCandidacyEvaluations } from "@/hooks/sets/useSetCandidacyEvaluations";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import {
  CandidacyEvaluation,
  EvaluationBlockType,
  EvaluationScoringType,
} from "@/types";

const questionSchema = z.object({
  id: z.string(),
  answer: z.boolean().optional(),
  question: z.string(),
  required: z.boolean(),
  type: z.enum(["binary"]).optional(),
});
const blockSchema = z.object({
  id: z.string(),
  blockType: z.nativeEnum(EvaluationBlockType),
  competency: z.string(),
  description: z.string(),
  notes: z.string().nullable(),
  questions: z.array(questionSchema),
  score: z.number().or(z.boolean()).nullable(),
  scoringType: z.nativeEnum(EvaluationScoringType).optional(),
});
const evaluationSchema = z.object({
  blocks: z.array(blockSchema),
  evaluationScore: z.number().nullable(),
  evaluationSummary: z.string().nullable(),
  miscNotes: z.string().nullable(),
});

export const OldEvaluationForm = ({
  candidacyId,
  evaluation,
  evaluations,
}: {
  candidacyId: string;
  evaluation: CandidacyEvaluation;
  evaluations: CandidacyEvaluation[];
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: updateCandidacy } = useUpdateCandidacy();
  const setCandidacyEvaluations = useSetCandidacyEvaluations();

  /* React Hooks */
  const [
    isDeleteEvaluationConfirmationDialogOpen,
    setIsDeleteEvaluationConfirmationDialogOpen,
  ] = useState(false);

  // Initialize form with evaluation values
  const form = useForm<z.infer<typeof evaluationSchema>>({
    resolver: zodResolver(evaluationSchema),
    defaultValues: {
      blocks: evaluation.blocks.map((b) => ({
        id: b.id,
        blockType: b.blockType,
        competency: b.competency,
        description: b.description,
        notes: b.notes,
        questions: b.questions.map((q) => ({
          id: q.id,
          answer: q.answer,
          question: q.question,
          required: q.required,
          type: q.type,
        })),
        score: b.score,
        scoringType: b.scoringType,
      })),
      evaluationScore: evaluation.evaluationScore,
      evaluationSummary: evaluation.evaluationSummary,
      miscNotes: evaluation.miscNotes,
    },
  });

  const { fields: blockFields } = useFieldArray({
    control: form.control,
    name: "blocks",
  });

  const blocksSnapshot = (blocks: z.infer<typeof blockSchema>[]) => {
    return JSON.stringify(
      blocks.map((b) => ({
        notes: b.notes,
        questions: b.questions.map((q) => ({ id: q.id, answer: q.answer })),
        score: b.score,
      })),
    );
  };

  const [debouncedBlocks] = useDebounce(
    blocksSnapshot(form.watch("blocks")),
    250,
  );
  const [debouncedEvaluationScore] = useDebounce(
    form.watch("evaluationScore"),
    250,
  );
  const [debouncedSummary] = useDebounce(form.watch("evaluationSummary"), 250);
  const [debouncedMiscNotes] = useDebounce(form.watch("miscNotes"), 250);

  const lastSavedBlocksRef = useRef(blocksSnapshot(evaluation.blocks));
  const lastSavedEvaluationScoreRef = useRef(evaluation.evaluationScore);
  const lastSavedSummaryRef = useRef(evaluation.evaluationSummary);
  const lastSavedMiscNotesRef = useRef(evaluation.miscNotes);

  // Necessary to prevent overlapping saves
  const isSavingRef = useRef(false);

  // Autosave all block sections with in-flight guard isSavingRef
  useEffect(() => {
    // Prevent continuous re-renders
    if (
      debouncedBlocks === lastSavedBlocksRef.current &&
      debouncedEvaluationScore === lastSavedEvaluationScoreRef.current &&
      debouncedMiscNotes === lastSavedMiscNotesRef.current &&
      debouncedSummary === lastSavedSummaryRef.current
    )
      return;

    // Avoid overlapping saves
    if (isSavingRef.current) return;

    const newEval: CandidacyEvaluation = {
      ...evaluation,
      ...(debouncedBlocks !== lastSavedBlocksRef.current && {
        blocks: form.getValues("blocks"),
      }),
      ...(debouncedEvaluationScore !== lastSavedEvaluationScoreRef.current && {
        evaluationScore: debouncedEvaluationScore,
      }),
      ...(debouncedSummary !== lastSavedSummaryRef.current && {
        evaluationSummary: debouncedSummary,
      }),
      ...(debouncedMiscNotes !== lastSavedMiscNotesRef.current && {
        miscNotes: debouncedMiscNotes,
      }),
    };

    isSavingRef.current = true;
    mixpanelUtils.trackFormSubmission(
      "Evaluation autosave",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    updateCandidacy(
      {
        candidacyId,
        updateCandidacyRequest: {
          evaluations: evaluations.map((e) =>
            e.id === evaluation.id ? newEval : e,
          ),
        },
      },
      {
        onSuccess: (candidacy) => {
          setCandidacyEvaluations({
            candidacyId,
            evaluations: candidacy.evaluations,
          });
          lastSavedBlocksRef.current = blocksSnapshot(newEval.blocks);
          lastSavedEvaluationScoreRef.current = newEval.evaluationScore;
          lastSavedSummaryRef.current = newEval.evaluationSummary;
          lastSavedMiscNotesRef.current = newEval.miscNotes;
          isSavingRef.current = false;
        },
        onError: () => {
          isSavingRef.current = false;
        },
      },
    );
  }, [
    candidacyId,
    debouncedBlocks,
    debouncedEvaluationScore,
    debouncedSummary,
    debouncedMiscNotes,
    evaluation,
    evaluations,
    form,
    setCandidacyEvaluations,
    updateCandidacy,
  ]);

  /* Handlers */
  const handleDeleteEvaluation = () => {
    mixpanelUtils.trackButtonClick(
      "Delete Evaluation",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    updateCandidacy(
      {
        candidacyId,
        updateCandidacyRequest: {
          evaluations: evaluations.filter((e) => e.id !== evaluation.id),
        },
      },
      {
        onSuccess: (candidacy) => {
          setCandidacyEvaluations({
            candidacyId,
            evaluations: candidacy.evaluations,
          });
          setIsDeleteEvaluationConfirmationDialogOpen(false);
        },
      },
    );
  };

  /* Render */
  const render = () => {
    return (
      <Form {...form}>
        <form className="flex flex-col gap-3 rounded-lg border border-slate-200 p-4">
          <Row className="mb-1 items-center justify-between">
            <Label className="text-sm font-semibold text-slate-700">
              {evaluation.name}
            </Label>
            <Button
              className="text-slate-400 hover:text-red-600"
              onClick={() => {
                setIsDeleteEvaluationConfirmationDialogOpen(true);
              }}
              size="icon"
              type="button"
              variant="ghost"
            >
              <Trash2Icon />
            </Button>
          </Row>
          {/* Misc Notes block */}
          <CollapsibleBlock
            description="This is a miscellaneous block. Add useful notes that don't correspond to a specific competency."
            form={form}
            name="miscNotes"
            title="Misc Notes"
          />

          {/* Middle competency blocks */}
          {blockFields.map((block, index) => {
            return (
              <CollapsibleBlock
                key={`block-${block.id}`}
                blockIndex={index}
                blockType={block.blockType}
                description={block.description}
                form={form}
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                name={`blocks.${index}.notes`}
                questions={block.questions}
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                scoreFieldName={`blocks.${index}.score`}
                scoringType={block.scoringType}
                title={block.competency}
              />
            );
          })}

          {/* Evaluation Summary block */}
          <CollapsibleBlock
            description="This is a summary block. Add a summary of the entire evaluation."
            form={form}
            isSummary
            name="evaluationSummary"
            scoreFieldName="evaluationScore"
            title="Evaluation Summary"
          />
          <ConfirmationDialog
            confirmationButtonVariant="destructive"
            confirmButtonText="Delete"
            description="This action can't be undone."
            onConfirm={handleDeleteEvaluation}
            onOpenChange={setIsDeleteEvaluationConfirmationDialogOpen}
            open={isDeleteEvaluationConfirmationDialogOpen}
            title="Are you sure you want to delete this evaluation?"
          />
        </form>
      </Form>
    );
  };

  return render();
};

const CollapsibleBlock = ({
  blockIndex,
  blockType,
  description,
  form,
  isSummary,
  name,
  questions,
  scoreFieldName,
  scoringType,
  title,
}: {
  blockIndex?: number;
  blockType?: EvaluationBlockType;
  description: string;
  form: ReturnType<typeof useForm<z.infer<typeof evaluationSchema>>>;
  isSummary?: boolean;
  name: FieldPath<z.infer<typeof evaluationSchema>>;
  questions?: z.infer<typeof questionSchema>[];
  scoreFieldName?: FieldPath<z.infer<typeof evaluationSchema>>;
  scoringType?: EvaluationScoringType;
  title: string;
}) => {
  /* Render */
  const renderQuestions = () => {
    if (!questions) return null;

    if (blockType === EvaluationBlockType.OPENENDED) {
      return (
        <Collapsible className="group/questions" defaultOpen>
          <Row className="items-center gap-1">
            <Label className="text-xs font-normal text-slate-500">
              Questions
            </Label>
            <span className="text-xs text-slate-500">({questions.length})</span>
            <CollapsibleTrigger asChild>
              <Button
                className="h-auto p-1 text-slate-500 hover:bg-transparent"
                variant="ghost"
              >
                <ChevronDownIcon className="group-data-[state=open]/questions:rotate-180" />
              </Button>
            </CollapsibleTrigger>
          </Row>
          <CollapsibleContent>
            <Column className="gap-0.5 border-l border-slate-200">
              {questions.map((q, i) => (
                <Row
                  className="ml-4 items-center gap-2 text-xs text-slate-500"
                  key={q.id}
                >
                  <span className="w-4">{i + 1}.</span>
                  <span>{q.question}</span>
                  {q.required && (
                    <span className="rounded-md border border-slate-500 px-1 text-[8px] text-slate-600">
                      Required
                    </span>
                  )}
                </Row>
              ))}
            </Column>
          </CollapsibleContent>
        </Collapsible>
      );
    }

    if (
      blockType === EvaluationBlockType.STRUCTURED &&
      blockIndex !== undefined
    ) {
      return (
        <Column className="mt-2 gap-2 border-l border-slate-200">
          {questions.map((q, i) => (
            <FormField
              key={q.id}
              control={form.control}
              // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
              name={`blocks.${blockIndex}.questions.${i}.answer`}
              render={({ field }) => (
                <Row className="items-center gap-3">
                  <Input
                    type="checkbox"
                    className="ml-4 h-3 w-3"
                    checked={field.value}
                    onChange={field.onChange}
                  />
                  <span className="text-xs text-slate-500">{q.question}</span>
                </Row>
              )}
            />
          ))}
        </Column>
      );
    }

    return null;
  };

  const render = () => {
    return (
      <Collapsible className="group/block" defaultOpen>
        <Column className="gap-3 rounded-lg border border-slate-200 p-6">
          <Row className="justify-between">
            <div>
              <Label className="text-base font-semibold text-slate-800">
                {title}
              </Label>
              <p className="text-sm text-slate-600">{description}</p>
              {renderQuestions()}
            </div>
            <Row className="items-center gap-2">
              <RatingInput
                form={form}
                isSummary={isSummary}
                scoreFieldName={scoreFieldName}
                scoringType={scoringType}
              />
              <CollapsibleTrigger asChild>
                <Button
                  className="hover:bg-transparent"
                  size="icon"
                  variant="ghost"
                >
                  <ChevronDownIcon className="group-data-[state=open]/block:rotate-180" />
                </Button>
              </CollapsibleTrigger>
            </Row>
          </Row>
          <CollapsibleContent>
            <div className="border-l border-slate-200 pl-3">
              <FormField
                control={form.control}
                name={name}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <RichTextEditorInput
                        editorContentClasses="pb-20"
                        mode={{
                          onChange: field.onChange,
                          type: "html",
                          value: (field.value ?? "") as string,
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CollapsibleContent>
        </Column>
      </Collapsible>
    );
  };

  return render();
};

const RatingInput = ({
  form,
  isSummary,
  scoreFieldName,
  scoringType,
}: {
  form: ReturnType<typeof useForm<z.infer<typeof evaluationSchema>>>;
  isSummary?: boolean;
  scoreFieldName?: FieldPath<z.infer<typeof evaluationSchema>>;
  scoringType?: EvaluationScoringType;
}) => {
  /* Render */
  const render = () => {
    if (!scoreFieldName) return null;

    if (scoringType === EvaluationScoringType.RATING || isSummary) {
      return <FormStarRatingInput fieldName={scoreFieldName} form={form} />;
    }

    if (scoringType === EvaluationScoringType.BINARY) {
      return (
        <FormField
          control={form.control}
          name={scoreFieldName}
          render={({ field }) => (
            <Row className="items-center justify-center gap-4">
              <Button
                type="button"
                variant="ghost"
                className="p-1"
                onClick={() => {
                  mixpanelUtils.trackButtonClick(
                    "Thumbs Up Rating",
                    MIXPANEL_SCREENS.CANDIDATE_SHEET,
                  );
                  field.onChange(field.value ? null : true);
                }}
              >
                <ThumbsUpIcon
                  className={cn(
                    field.value
                      ? "fill-green-500 text-green-500"
                      : "fill-slate-300 text-slate-300",
                  )}
                />
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="!m-0 p-1"
                onClick={() => {
                  mixpanelUtils.trackButtonClick(
                    "Thumbs Down Rating",
                    MIXPANEL_SCREENS.CANDIDATE_SHEET,
                  );
                  field.onChange(field.value === false ? null : false);
                }}
              >
                <ThumbsDownIcon
                  className={cn(
                    field.value === false
                      ? "fill-red-500 text-red-500"
                      : "fill-slate-300 text-slate-300",
                  )}
                />
              </Button>
            </Row>
          )}
        />
      );
    }

    if (scoringType === EvaluationScoringType.COMPLETION) {
      return (
        <FormField
          control={form.control}
          name={scoreFieldName}
          render={({ field }) => (
            <Row className="items-center gap-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  mixpanelUtils.trackButtonClick(
                    "Completion Checkbox",
                    MIXPANEL_SCREENS.CANDIDATE_SHEET,
                  );
                  field.onChange(!field.value);
                }}
              >
                <CheckIcon
                  strokeWidth={3}
                  className={cn(
                    "!h-5 !w-5",
                    field.value ? "text-green-500" : "text-slate-300",
                  )}
                />
              </Button>
            </Row>
          )}
        />
      );
    }

    return null;
  };

  return render();
};
